import {createAction, props} from '@ngrx/store';
import {IDebtorDetails} from '../models/debtors';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {IQuoteComposeEntry} from '../models/quote-compose-entry.interface';
import {IQuoteListItem} from '../models/quote-list-item.interface';
import {IQuoteStockItem} from '../models/quoteStockItems';

/**
 * Action to add a named section to the quote.
 */
export const addQuoteSection = createAction(
  '[Quotes] Add Quote Section',
  props<{ id: string; name: string }>(),
);

/**
 * Action to rename a quote section.
 */
export const updateQuoteSection = createAction(
  '[Quotes] Update Quote Section',
  props<{ id: string; name: string }>(),
);

/**
 * Action to remove a quote section.
 */
export const removeQuoteSection = createAction(
  '[Quotes] Remove Quote Section',
  props<{ id: string }>(),
);

/**
 * Action to replace the full ordered compose list.
 */
export const loadQuoteComposeEntries = createAction(
  '[Quotes] Load Quote Compose Entries',
  props<{ entries: IQuoteComposeEntry[] }>(),
);

/**
 * Action to add a stock item to the quote.
 */
export const addQuoteItem = createAction(
  '[Quotes] Add Quote Item',
  props<{ item: IQuoteStockItem }>(),
);
/**
 * Action to add a stock item to the quote.
 */
export const addQuoteItemsArray = createAction(
  '[Quotes] Add Quote Items Array',
  props<{ items: IQuoteStockItem[] }>(),
);

/**
 * Action to update a quote item.
 */
export const updateQuoteItem = createAction(
  '[Quotes] Update Quote Item',
  props<{ code: string; changes: Partial<IQuoteStockItem> }>(),
);

/**
 * Action to remove a stock item from the quote.
 */
export const removeQuoteItem = createAction(
  '[Quotes] Remove Quote Item',
  props<{ code: string }>(),
);

/**
 * Action to remove multiple stock items from the quote.
 */
export const removeQuoteItems = createAction(
  '[Quotes] Remove Quote Items',
  props<{ codes: string[] }>(),
);

/**
 * Action to clear all quote items.
 */
export const clearQuote = createAction(
  '[Quotes] Clear Quote',
);

/**
 * Action to load quote items (e.g., from saved quote).
 */
export const loadQuoteItems = createAction(
  '[Quotes] Load Quote Items',
  props<{ items: IQuoteStockItem[] }>(),
);

/**
 * Action to set the selected debtor for the quote.
 */
export const setQuoteDebtor = createAction(
  '[Quotes] Set Quote Debtor',
  props<{ debtor: IDebtorDetails }>(),
);

/**
 * Action to clear the selected debtor.
 */
export const clearQuoteDebtor = createAction(
  '[Quotes] Clear Quote Debtor',
);

/**
 * Action to set the comment for the quote.
 */
export const setQuoteComment = createAction(
  '[Quotes] Set Quote Comment',
  props<{ comment: string }>(),
);

/**
 * Action to clear the quote comment.
 */
export const clearQuoteComment = createAction(
  '[Quotes] Clear Quote Comment',
);

/**
 * Action to process a quote and save to Firebase.
 */
export const processQuote = createAction(
  '[Quotes] Process Quote',
);

/**
 * Action to load an existing quote's metadata and line items.
 */
export const loadQuoteDetail = createAction(
  '[Quotes] Load Quote Detail',
  props<{ quoteId: string }>(),
);

export const loadQuoteDetailSuccess = createAction(
  '[Quotes] Load Quote Detail Success',
  props<{ quoteId: string }>(),
);

export const loadQuoteDetailFailure = createAction(
  '[Quotes] Load Quote Detail Failure',
  props<{ error: IGlobalError }>(),
);

/**
 * Action dispatched when quote processing succeeds.
 */
export const processQuoteSuccess = createAction(
  '[Quotes] Process Quote Success',
  props<{ quoteId: string; messageId: string; localDraftId?: string | null }>(),
);

/**
 * Action dispatched when quote processing fails.
 */
export const processQuoteFailure = createAction(
  '[Quotes] Process Quote Failure',
  props<{ error: IGlobalError }>(),
);

/**
 * Action to set visible fields for the quote grid.
 */
export const setQuotesVisibleFields = createAction(
  '[Quotes] Set Visible Fields',
  props<{ visibleFields: string[] }>(),
);

/**
 * Action to download quote items as CSV.
 */
export const downloadQuotesAsCSV = createAction(
  '[Quotes] Download Quotes As CSV',
);

/**
 * Action dispatched when quote CSV download succeeds.
 */
export const downloadQuotesAsCSVSuccess = createAction(
  '[Quotes] Download Quotes As CSV Success',
);
/**
 * Action dispatched when quote CSV download succeeds.
 */
export const updateLocalStorage = createAction(
  '[Quotes] Update LocalStorage',
);

/**
 * Action dispatched when the quotes feature is initialised (list page opened).
 */
export const initQuotesFeature = createAction(
  '[Quotes] Init Feature',
);

/**
 * Records which store the quotes feature data is currently bound to.
 */
export const setQuotesLoadedStoreId = createAction(
  '[Quotes] Set Loaded Store Id',
  props<{ storeId: string | null }>(),
);

/**
 * Action dispatched when the user leaves the quotes feature.
 */
export const clearQuotesFeatureState = createAction(
  '[Quotes] Clear Feature State',
);

/**
 * Action to load the quotes list for the selected store.
 */
export const loadQuotesList = createAction(
  '[Quotes] Load Quotes List',
);

/**
 * Subscribes to real-time Firestore updates for the quotes list.
 */
export const subscribeQuotesList = createAction(
  '[Quotes] Subscribe Quotes List',
);

export const loadQuotesListSuccess = createAction(
  '[Quotes] Load Quotes List Success',
  props<{ quotes: IQuoteListItem[] }>(),
);

export const loadQuotesListFailure = createAction(
  '[Quotes] Load Quotes List Failure',
  props<{ error: IGlobalError }>(),
);

/**
 * Action to load debtors (and contacts) for the selected store into quotes state.
 */
export const loadQuoteDebtors = createAction(
  '[Quotes] Load Quote Debtors',
);

export const loadQuoteDebtorsSuccess = createAction(
  '[Quotes] Load Quote Debtors Success',
  props<{ debtors: Record<string, IDebtorDetails & { debtorId?: string; isDebtorSelected?: boolean }> }>(),
);

export const loadQuoteDebtorsFailure = createAction(
  '[Quotes] Load Quote Debtors Failure',
  props<{ error: IGlobalError }>(),
);

/**
 * Action dispatched after debtor selection to open the quote compose grid.
 */
export const beginNewQuote = createAction(
  '[Quotes] Begin New Quote',
  props<{ debtor: IDebtorDetails; draftId: string }>(),
);

/**
 * Action to resume a quote draft from localStorage.
 */
export const resumeLocalQuoteDraft = createAction(
  '[Quotes] Resume Local Quote Draft',
  props<{ draftId: string }>(),
);

export const resumeLocalQuoteDraftSuccess = createAction(
  '[Quotes] Resume Local Quote Draft Success',
  props<{ draftId: string }>(),
);

export const resumeLocalQuoteDraftFailure = createAction(
  '[Quotes] Resume Local Quote Draft Failure',
  props<{ error: IGlobalError }>(),
);

/**
 * Refreshes local draft rows shown in the quotes list.
 */
export const refreshLocalDraftQuotes = createAction(
  '[Quotes] Refresh Local Draft Quotes',
);

export const refreshLocalDraftQuotesSuccess = createAction(
  '[Quotes] Refresh Local Draft Quotes Success',
  props<{ drafts: IQuoteListItem[] }>(),
);

/**
 * Deletes all local quote drafts for the current store.
 */
export const clearAllLocalQuoteDrafts = createAction(
  '[Quotes] Clear All Local Quote Drafts',
);

/**
 * Sets the active local draft id for the current compose session.
 */
export const setActiveQuoteDraftId = createAction(
  '[Quotes] Set Active Quote Draft Id',
  props<{ draftId: string | null }>(),
);
